'use client'

import { motion, AnimatePresence, HTMLMotionProps } from 'framer-motion'
import { ReactNode, CSSProperties, type ComponentType } from 'react'
import type { ComponentPropsWithoutRef } from 'react'
import { Text, Title, Box, Card, Button, Stack, Group, List, Flex } from '@mantine/core'
import {
    fadeInVariants,
    slideVariants,
    gentleSlideVariants,
    scaleVariants,
    staggerContainer,
    staggerItem,
    smoothTransition,
    gentleTransition,
    springTransition,
    hoverScale,
    hoverScaleSmall,
    hoverBackground,
} from '@/lib/animations'

// Semantic animation components for common use cases

/**
 * FadeInSection - Animate content fading in from a direction
 *
 * @example
 * // Basic usage - fades in from bottom
 * <FadeInSection>
 *   <Title>My Header</Title>
 * </FadeInSection>
 *
 * @example
 * // With delay and custom direction
 * <FadeInSection delay={0.3} direction="left">
 *   <Text>This slides in from the left after 0.3s</Text>
 * </FadeInSection>
 *
 * @example
 * // Common pattern - staggered sections
 * <FadeInSection delay={0.1}>
 *   <Breadcrumbs>...</Breadcrumbs>
 * </FadeInSection>
 * <FadeInSection delay={0.2}>
 *   <Title>Page Title</Title>
 * </FadeInSection>
 * <FadeInSection delay={0.3}>
 *   <Text>Description</Text>
 * </FadeInSection>
 */

interface FadeInSectionProps {
    children: ReactNode
    delay?: number
    direction?: 'up' | 'down' | 'left' | 'right'
    className?: string
    style?: CSSProperties
}

/**
 * HoverScaleCard - Card that scales on hover with optional click handling
 *
 * @example
 * // Basic hover card
 * <HoverScaleCard>
 *   <Box p="md">Card content</Box>
 * </HoverScaleCard>
 *
 * @example
 * // Clickable card with stronger scale
 * <HoverScaleCard
 *   onClick={() => navigate('/details')}
 *   scaleAmount="medium"
 * >
 *   <CareListCard title="Click me!" />
 * </HoverScaleCard>
 *
 * @example
 * // Service cards, product cards, list items
 * <HoverScaleCard onClick={() => selectService(service.id)}>
 *   <ServiceInfoCard {...service} />
 * </HoverScaleCard>
 */

interface HoverScaleCardProps {
    children: ReactNode
    onClick?: () => void
    scaleAmount?: 'subtle' | 'medium' // subtle = 1.01, medium = 1.02
    className?: string
    style?: CSSProperties
}

/**
 * ActionItem - List item with background hover effect
 *
 * @example
 * // Basic action item
 * <ActionItem>
 *   <Flex align="center" gap="sm" py="md">
 *     <IconCheck />
 *     <Text>Mark as complete</Text>
 *   </Flex>
 * </ActionItem>
 *
 * @example
 * // Clickable action with custom background
 * <ActionItem
 *   onClick={() => markComplete()}
 *   hoverBackground={false}
 * >
 *   <Button variant="subtle">Custom Action</Button>
 * </ActionItem>
 *
 * @example
 * // Common pattern - action list
 * <Stack gap={0}>
 *   <ActionItem onClick={() => approve()}>
 *     <Flex><IconCheck />Approve</Flex>
 *   </ActionItem>
 *   <Divider />
 *   <ActionItem onClick={() => decline()}>
 *     <Flex><IconX />Decline</Flex>
 *   </ActionItem>
 * </Stack>
 */

interface ActionItemProps {
    children: ReactNode
    onClick?: () => void
    hoverBackground?: boolean // default: true
    className?: string
    style?: CSSProperties
}

/**
 * DividerAnimation - Animated divider that scales in
 *
 * @example
 * // Basic divider animation
 * <DividerAnimation>
 *   <Divider />
 * </DividerAnimation>
 *
 * @example
 * // Delayed divider between sections
 * <DividerAnimation delay={0.5}>
 *   <ThickDivider />
 *   <Space h="lg" />
 * </DividerAnimation>
 *
 * @example
 * // Vertical divider
 * <DividerAnimation direction="vertical">
 *   <Divider orientation="vertical" />
 * </DividerAnimation>
 */

interface DividerAnimationProps {
    children: ReactNode
    delay?: number
    direction?: 'horizontal' | 'vertical'
    className?: string
    style?: CSSProperties
}

interface AnimatedContainerProps {
    children: ReactNode
    className?: string
    style?: CSSProperties
    delay?: number
    variant?: 'fade' | 'scale'
}

interface AnimatedTransitionProps {
    children: ReactNode
    uniqueKey: string
    className?: string
    style?: CSSProperties
    direction?: 'horizontal' | 'vertical'
    gentle?: boolean
}

interface AnimatedButtonProps {
    children: ReactNode
    onClick?: () => void
    className?: string
    style?: CSSProperties
    disabled?: boolean
    scaleAmount?: 'small' | 'medium'
}

interface AnimatedCardProps {
    children: ReactNode
    onClick?: () => void
    className?: string
    style?: CSSProperties
    hoverEffect?: 'scale' | 'background' | 'both'
}

/**
 * AnimatedStagger - Container for automatically sequenced list animations
 *
 * @example
 * // Basic usage - animates children in sequence
 * <AnimatedStagger>
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 *   <div>Item 3</div>
 * </AnimatedStagger>
 *
 * @example
 * // With dynamic list (most common)
 * <AnimatedStagger>
 *   {prescriptions.map(prescription => (
 *     <AnimatedStaggerItem key={prescription.id}>
 *       <CareListCard
 *         title={prescription.name}
 *         subtitle={prescription.dueDate}
 *       />
 *     </AnimatedStaggerItem>
 *   ))}
 * </AnimatedStagger>
 *
 */

interface AnimatedStaggerProps {
    children: ReactNode
    className?: string
    style?: CSSProperties
}

interface AnimatedStaggerItemProps {
    children: ReactNode
    className?: string
    style?: CSSProperties
}

// Basic animated container with fade-in effect
export function AnimatedContainer({ children, className, style, delay = 0, variant = 'fade' }: AnimatedContainerProps) {
    const variants = variant === 'fade' ? fadeInVariants : scaleVariants

    return (
        <motion.div
            variants={variants}
            initial="initial"
            animate="animate"
            className={className}
            style={{
                ...style,
                ...(delay > 0 && {
                    animationDelay: `${delay}s`,
                }),
            }}
        >
            {children}
        </motion.div>
    )
}

// Animated transition wrapper with AnimatePresence
export function AnimatedTransition({
    children,
    uniqueKey,
    className,
    style,
    direction = 'horizontal',
    gentle = false,
}: AnimatedTransitionProps) {
    const getVariants = () => {
        if (direction === 'horizontal') {
            return gentle ? gentleSlideVariants : slideVariants
        } else {
            return gentle
                ? {
                      initial: { opacity: 0, y: 8, scale: 0.999 },
                      in: { opacity: 1, y: 0, scale: 1 },
                      out: { opacity: 0, y: -6, scale: 1.001 },
                  }
                : {
                      initial: { opacity: 0, y: 20, scale: 0.98 },
                      in: { opacity: 1, y: 0, scale: 1 },
                      out: { opacity: 0, y: -20, scale: 1.02 },
                  }
        }
    }

    const transition = gentle ? gentleTransition : smoothTransition

    return (
        <AnimatePresence mode="wait">
            <motion.div
                key={uniqueKey}
                variants={getVariants()}
                initial="initial"
                animate="in"
                exit="out"
                transition={transition}
                className={className}
                style={style}
            >
                {children}
            </motion.div>
        </AnimatePresence>
    )
}

// Animated button with hover and tap effects
export function AnimatedButton({
    children,
    onClick,
    className,
    style,
    disabled = false,
    scaleAmount = 'medium',
}: AnimatedButtonProps) {
    const hoverVariant = scaleAmount === 'small' ? hoverScaleSmall : hoverScale

    return (
        <motion.button
            onClick={onClick}
            disabled={disabled}
            whileHover={disabled ? {} : hoverVariant}
            whileTap={disabled ? {} : { scale: 0.98 }}
            className={className}
            style={style}
            transition={springTransition}
        >
            {children}
        </motion.button>
    )
}

// Animated card with hover effects
export function AnimatedCard({ children, onClick, className, style, hoverEffect = 'scale' }: AnimatedCardProps) {
    const getHoverProps = () => {
        switch (hoverEffect) {
            case 'scale':
                return { whileHover: hoverScaleSmall }
            case 'background':
                return { whileHover: hoverBackground }
            case 'both':
                return {
                    whileHover: {
                        ...hoverScaleSmall,
                        ...hoverBackground,
                    },
                }
            default:
                return {}
        }
    }

    return (
        <motion.div
            onClick={onClick}
            className={className}
            style={style}
            transition={springTransition}
            whileTap={onClick ? { scale: 0.98 } : {}}
            {...getHoverProps()}
        >
            {children}
        </motion.div>
    )
}

// Animated stagger container for lists
export function AnimatedStagger({ children, className, style }: AnimatedStaggerProps) {
    return (
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" className={className} style={style}>
            {children}
        </motion.div>
    )
}

// Animated stagger item (use inside AnimatedStagger)
export function AnimatedStaggerItem({ children, className, style }: AnimatedStaggerItemProps) {
    return (
        <motion.div variants={staggerItem} className={className} style={style}>
            {children}
        </motion.div>
    )
}

// Fade in section with configurable delay and direction
export function FadeInSection({ children, delay = 0, direction = 'up', className, style }: FadeInSectionProps) {
    const directionMap = {
        up: { opacity: 0, y: 20 },
        down: { opacity: 0, y: -20 },
        left: { opacity: 0, x: 20 },
        right: { opacity: 0, x: -20 },
    }

    return (
        <motion.div
            initial={directionMap[direction]}
            animate={{ opacity: 1, y: 0, x: 0 }}
            transition={{ delay, ...smoothTransition }}
            className={className}
            style={style}
        >
            {children}
        </motion.div>
    )
}

// Card with hover scale effect
export function HoverScaleCard({ children, onClick, scaleAmount = 'subtle', className, style }: HoverScaleCardProps) {
    const scaleValue = scaleAmount === 'subtle' ? 1.01 : 1.02

    return (
        <motion.div
            onClick={onClick}
            whileHover={{ scale: scaleValue }}
            whileTap={onClick ? { scale: 0.98 } : {}}
            transition={springTransition}
            className={className}
            style={style}
        >
            {children}
        </motion.div>
    )
}

// Action item with background hover effect
export function ActionItem({ children, onClick, hoverBackground = true, className, style }: ActionItemProps) {
    return (
        <motion.div
            onClick={onClick}
            whileHover={hoverBackground ? { backgroundColor: 'rgba(0,0,0,0.05)' } : {}}
            transition={{ duration: 0.2 }}
            className={className}
            style={style}
        >
            {children}
        </motion.div>
    )
}

// Divider with scale animation
export function DividerAnimation({
    children,
    delay = 0,
    direction = 'horizontal',
    className,
    style,
}: DividerAnimationProps) {
    const scaleAxis = direction === 'horizontal' ? 'scaleX' : 'scaleY'

    return (
        <motion.div
            initial={{ opacity: 0, [scaleAxis]: 0 }}
            animate={{ opacity: 1, [scaleAxis]: 1 }}
            transition={{ delay, duration: 0.3 }}
            className={className}
            style={style}
        >
            {children}
        </motion.div>
    )
}

// Simple animated div with common props (keep for edge cases)
export function AnimatedDiv({
    children,
    className,
    style,
    initial = {},
    animate = {},
    exit = {},
    transition = smoothTransition,
    whileHover = {},
    whileTap = {},
    ...props
}: {
    children: ReactNode
    className?: string
    style?: CSSProperties
    initial?: any
    animate?: any
    exit?: any
    transition?: any
    whileHover?: any
    whileTap?: any
    [key: string]: any
}) {
    return (
        <motion.div
            initial={initial}
            animate={animate}
            exit={exit}
            transition={transition}
            whileHover={whileHover}
            whileTap={whileTap}
            className={className}
            style={style}
            {...props}
        >
            {children}
        </motion.div>
    )
}

// Motion-wrapped Mantine components for passing motion props and mantine props (variants, initial, animate, etc.)
// Use these when you need Mantine layout/typography with framer-motion animation props.
type MotionComponentProps<T extends ComponentType<any>> = ComponentPropsWithoutRef<T> & HTMLMotionProps<'div'>

function createMotionComponent<T extends ComponentType<any>>(Component: T) {
    return motion(Component as ComponentType<any>) as ComponentType<MotionComponentProps<T>>
}

export const MotionText = createMotionComponent(Text)
export const MotionTitle = createMotionComponent(Title)
export const MotionBox = createMotionComponent(Box)
export const MotionCard = createMotionComponent(Card)
export const MotionButton = createMotionComponent(Button)
export const MotionStack = createMotionComponent(Stack)
export const MotionGroup = createMotionComponent(Group)
export const MotionList = createMotionComponent(List)
export const MotionFlex = createMotionComponent(Flex)
export const MotionDiv = motion.div

export type { MotionComponentProps }
